import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import HERO_BG from "@/lib/heroBg";
import { ArrowRight, Feather, MessagesSquare, CalendarClock } from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user ? "/archive" : "/register";
  const ctaLabel = user ? "Enter the Archive" : "Join the Reading";

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-ink/60" aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" aria-hidden />

        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-20 w-full">
          <div className="max-w-3xl animate-fade-up">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-parchment/80 mb-6">
              An 1872 Manuscript · Read Together
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-parchment leading-[0.98] tracking-tight">
              Turning the pages of the
              <span className="italic text-highlight"> Underground Railroad</span>, one week at a time.
            </h1>
            <p className="font-body text-base md:text-lg text-parchment/85 mt-8 max-w-2xl leading-relaxed">
              Each week a new page from a fragile 1872 volume is unsealed and shared with the
              circle. Read the original scan, weigh the words, and add your voice to the
              discussion that surrounds it.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link
                to={ctaTo}
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 bg-oxblood text-parchment px-8 py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors"
              >
                {ctaLabel}
                <ArrowRight strokeWidth={1.5} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              {!user && (
                <Link
                  to="/login"
                  data-testid="hero-signin"
                  className="font-mono text-xs tracking-widest uppercase text-parchment link-underline pb-1"
                >
                  Already a member? Sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-4">
            The Weekly Ritual
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">
            A slow, deliberate reading of history
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: CalendarClock,
              n: "01",
              title: "A page each week",
              body: "The archivist unseals one scanned leaf of the 1872 volume for the circle to study.",
            },
            {
              icon: Feather,
              n: "02",
              title: "Read the original",
              body: "View the page exactly as it was printed — ink, foxing, marginalia and all.",
            },
            {
              icon: MessagesSquare,
              n: "03",
              title: "Discuss together",
              body: "Leave your reading in the thread. Question, annotate, and respond to fellow members.",
            },
          ].map((c) => (
            <div
              key={c.n}
              className="bg-surface border border-archive-border p-8 offset-shadow"
              data-testid={`feature-card-${c.n}`}
            >
              <div className="flex items-center justify-between mb-6">
                <c.icon strokeWidth={1.5} className="w-7 h-7 text-oxblood" />
                <span className="font-mono text-xs tracking-widest text-text-muted">{c.n}</span>
              </div>
              <h3 className="font-serif text-2xl text-ink mb-3">{c.title}</h3>
              <p className="font-body text-text-muted leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-archive-border">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-mono text-[10px] tracking-widest uppercase text-text-muted">
            The UGRR Ledger · A community reading room
          </span>
          <span className="font-body italic text-text-muted text-sm">
            "Freedom, written page by page."
          </span>
        </div>
      </footer>
    </div>
  );
}
